import React from "react";
import SectionHeading from "./SectionHeading";

/**
 * Experiences grid — 7 capture formats Stori offers, each in a vertical
 * 9:16 card (matching the reference). Placeholder imagery for now;
 * client will swap in real videos / photos later.
 */
const EXPERIENCES = [
  {
    name: "Boomerang Videos",
    desc: "6-second branded loops engineered for Instagram and TikTok — the format guests share without thinking twice.",
    video:
      "https://customer-assets.emergentagent.com/job_guest-intelligence/artifacts/ks871ufc_boom.mp4",
  },
  {
    name: "Animated GIFs",
    desc: "AI-curated photo sequences that capture the moment in motion. On-brand, frictionless, instantly delivered.",
    video:
      "https://customer-assets.emergentagent.com/job_guest-intelligence/artifacts/nhbrbw29_dion.mp4",
  },
  {
    name: "Orbital 360 Booms",
    desc: "Cinematic 360° spins around your guest — the single highest-shared format in our network, by 4×.",
    video:
      "https://customer-assets.emergentagent.com/job_guest-intelligence/artifacts/pyxk7mk3_360.mp4",
  },
  {
    name: "Video Shoutouts",
    desc: "Up to 90 seconds of branded testimonial. Influencers deliver endorsements your team re-cuts for paid social.",
    video:
      "https://customer-assets.emergentagent.com/job_guest-intelligence/artifacts/hneuyvzf_shoutout.mp4",
  },
  {
    name: "Photo Selfies",
    desc: "Studio-grade HD portraits with AI-tuned lighting. The format every guest opts in for, every time.",
    media:
      "https://customer-assets.emergentagent.com/job_guest-intelligence/artifacts/axwdgr7f_photo.jpg",
  },
  {
    name: "Slow-Mo",
    desc: "1080p slow-motion captures that turn ordinary moments into hero footage built for the algorithm.",
    media:
      "https://customer-assets.emergentagent.com/job_guest-intelligence/artifacts/hmp5t7o3_slow-mo.gif",
  },
  {
    name: "AI Photos",
    desc: "AI-generated portraits and on-brand styles. Guests step in once and leave with a series of looks engineered for their feed.",
    media:
      "https://customer-assets.emergentagent.com/job_guest-intelligence/artifacts/a5oldf8f_ai.jpg",
  },
];

export default function ExperiencesGrid() {
  return (
    <section
      id="experiences"
      data-testid="experiences-grid"
      className="relative py-24 lg:py-32 border-b border-stori-border bg-stori-bg"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeading
          eyebrow="CAPTURE EXPERIENCES"
          title="Seven Capture Experiences"
          lead="Each experience is optimized for shareability, brand consistency, and guest data capture."
        />

        {/* Cards */}
        <div className="mt-14 lg:mt-16 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4 lg:gap-3">
          {EXPERIENCES.map((exp, i) => (
            <article
              key={exp.name}
              data-testid={`experience-${i}`}
              className="group flex flex-col"
            >
              <div className="relative aspect-[9/16] rounded-lg overflow-hidden border border-stori-border bg-stori-surface1">
                {exp.video ? (
                  <video
                    src={exp.video}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                    aria-label={exp.name}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <img
                    src={exp.media}
                    alt={exp.name}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                  />
                )}
                {/* Soft top→bottom overlay for legibility on hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-stori-bg/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                {/* Cyan corner indicator */}
                <span className="absolute top-3 right-3 w-1.5 h-1.5 rounded-full bg-stori-cyan opacity-0 group-hover:opacity-100 transition-opacity" />
                {/* Index pill */}
                <span className="absolute top-3 left-3 font-mono text-[9px] uppercase tracking-[0.2em] text-white/80 bg-stori-bg/60 backdrop-blur px-2 py-1 rounded">
                  0{i + 1}
                </span>
              </div>
              <h3 className="mt-4 font-heading text-lg lg:text-xl font-semibold text-white tracking-tight group-hover:text-stori-cyan transition-colors">
                {exp.name}
              </h3>
              <p className="mt-2 text-sm text-stori-muted leading-relaxed">
                {exp.desc}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
